package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/remember_saveable")
@Composable
fun RememberSaveablePage() {
    BlogLayout(
        PageProperties(
            "rememberSaveable",
            ""
        ),
        BlogProperties("Jetpack Compose", "rememberSaveable"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            //https://proandroiddev.com/explained-custom-savers-in-remembersaveable-for-jetpack-compose-070211eb3e92
            SpanText(
                text = "rememberSaveable - (Serializable UI State, Survive Rotation, SavedStateRegistry(Bundle, Parcelable, Serializable))",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The rememberSaveable API extends remember by persisting state across configuration changes. It automatically saves and restores values that can be stored in a Bundle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, state is usually stored in memory, Bundle-style state registry with remember. But if your activity is recreated (e.g., after configuration change like rotation, or process death + restore), that state is lost. rememberSaveable solves this by saving state to the SavedInstanceState bundle, so it can be restored later. Works automatically for basic types (Int, String, Boolean, etc.) and things that implement Parcelable or Serializable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable function is retained state across configuration changes or orientation changes and can outlive the composable itself. rememberSaveable works hand-in-hand with the SavedStateRegistry to automatically save and restore state. By default, it saves the entire object using Parcelable. But what if your model has more complex properties, and you want to optimize what gets saved? That’s where a custom Saver comes in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you don’t want to lose state on rotation, use rememberSaveable(). It can only store data types which are supported by Bundle (android.os.Bundle). Any custom data class is not supported by default.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Orientation changes in Jetpack Compose are handled automatically by recomposing the UI based on the new configuration. Composable functions that define the UI layout and behavior will be recomposed with the updated configuration, allowing the UI to adapt to the new orientation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use rememberSaveable{} to restore your UI state after an activity or process is recreated. RememberSaveable retains state across recompositions. In addition, rememberSaveable also retains state across activity and process recreation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example 1, Device rotation, switch from Light mode to Dark Mode and vice-versa, etc..., to survive both unpredictable recomposition and device configuration you can use “rememberSaveable” API.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Example 2, Imagine you have a text field where the user is typing a long message. If you use remember to store the text, it will be lost when the device rotates. But if you use rememberSaveable, the text will be persisted and restored when the user returns.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "rememberSaveable Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use rememberSaveable for state that needs to survive configuration changes, such as form inputs or navigation states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Saving state: When you use rememberSaveable, Compose automatically saves the state value provided to it in a Bundle, which is then stored in the Android SavedStateHandle. This saving occurs whenever the value changes or when the Composable function is recomposed due to changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Restoring state: When the Composable function is recomposed due to configuration changes or process death, Compose retrieves the saved state from the SavedStateHandle and restores the state value provided to rememberSaveable to its last saved state. This way, the app can resume with its previous state intact.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "rememberSaveable Internals",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    public fun <T : Any> rememberSaveable(
                        vararg inputs: Any?,
                        saver: Saver<T, out Any> = autoSaver(),
                        key: String? = null,
                        init: () -> T,
                    ): T {
                        val compositeKey = currentCompositeKeyHashCode
                        // key is the one provided by the user or the one generated by the compose runtime
                        val finalKey =
                            if (!key.isNullOrEmpty()) {
                                key
                            } else {
                                compositeKey.toString(MaxSupportedRadix)
                            }
                        @Suppress("UNCHECKED_CAST") (saver as Saver<T, Any>)

                        val registry = LocalSaveableStateRegistry.current

                        val holder = remember {
                            // value is restored using the registry or created via [init] lambda
                            val restored = registry?.consumeRestored(finalKey)?.let { saver.restore(it) }
                            val finalValue = restored ?: init()
                            SaveableHolder(saver, registry, finalKey, finalValue, inputs)
                        }

                        val value = holder.getValueIfInputsDidntChange(inputs) ?: init()
                        SideEffect { holder.update(saver, registry, finalKey, value, inputs) }

                        return value
                    }
                    
                   var bitmap by rememberSaveable (mutableStateOf<Bitmap?>(value = null) }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The rememberSaveable function extends the capabilities of remember by adding support for saving and restoring state across configuration changes and process death. Below is the breakdown of its internal implementation:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Generation: The key parameter allows users to provide a custom key. If none is provided, a composite key is automatically generated using the current composition’s hash.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Restoration: The LocalSaveableStateRegistry is used to retrieve previously saved values for the given key. If a saved value exists, it is restored using the provided Saver.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Default Value Initialization: If no restored value exists, the default value is initialized using the init lambda",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Saveable Holder: A SaveableHolder is created to manage the state, saver, registry, and inputs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Input Change Handling: If the inputs to rememberSaveable change, the state is invalidated, and the value is re-initialized.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Side Effects: The SideEffect ensures that the updated state is saved in the registry during recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is the internal implementation of rememberSaveable. Unlike remember, it goes a step further by preserving state across configuration changes, such as screen rotations, through the use of the LocalSaveableStateRegistry. Additionally, the saver parameter empowers developers to define custom serialization and deserialization logic, making it ideal for handling complex objects seamlessly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                   var bitmap by rememberSaveable (mutableStateOf<Bitmap?>(value = null) }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                   var headerState by rememberSaveable { mutableStateOf(MainHeaderContainerState.Title) }
                   val isSearch = rememberSaveable(headerState) { headerState == MainHeaderContainerState.Search }

                   var firstScrollPerformed by rememberSaveable(isSearch, searchQuery) { mutableStateOf(false) }
                """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                      @Composable
                      fun RememberSaveableExample() {
                          val savedText by rememberSaveable { mutableStateOf("Initial Text") }
                          TextField(
                              value = savedText,
                              onValueChange = { newText ->
                                  savedText.value = newText
                              }
                          )
                          Text(text = "Text Entered:  $savedText")
                      }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun ModalBottomSheet(onDismiss: () -> Unit) {
                        val skipPartiallyExpanded by remember { mutableStateOf(false) }
                        var edgeToEdgeEnabled by remember { mutableStateOf(false) }

                        val scope = rememberCoroutineScope()

                        val bottomSheetState = rememberModalBottomSheetState(
                            skipPartiallyExpanded = skipPartiallyExpanded
                        )

                        ModalBottomSheet(onDismissRequest = onDismiss, sheetState = bottomSheetState, dragHandle = {}) {

                        }
                    }

                    var openBottomSheet by rememberSaveable { mutableStateOf(false) }

                    if (openBottomSheet) {
                        ModalBottomSheet { openBottomSheet = false }
                    }

                    //openBottomSheet
                    MainDataBundleItem(
                    modifier = Modifier
                        .fillMaxWidth()
                        .wrapContentHeight()
                    ) {
                        openBottomSheet = true
                    }

                    @Composable
                    fun MainDataBundleItem(modifier: Modifier = Modifier, onSellBundleClick: () -> Unit) {

                    }
                    """.trimIndent()
            )

            SpanText(
                text = "This persists state across / Survives",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "recomposition - ✅",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "configuration changes - ✅",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "process death - ✅",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "…but only if the value is Parcelable/Serializable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What Happens Internally (Senior Insight)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose registers a state provider with SavedStateRegistry",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On destruction: State is written into a Bundle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "On recreation: State is restored before composition starts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why rememberSaveable is Slower Compared to remember",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Serialization cost",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bundle size limits",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Process boundary crossing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Never use rememberSaveable for large or frequently-changing objects",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tabs, selected filters, simple form state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Anything you would be sad to lose on rotation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When not to use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Large objects",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Complex graphs",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Things better owned by ViewModel",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State Persistence with rememberSaveable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State refers to data that may change during the lifetime of a composable function and needs to be preserved during recompositions. When the configuration changes, the default behavior is for the composable function to recompose from scratch, potentially losing the current state. To avoid this issue, you can use rememberSaveable to preserve and restore the state automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun MyComposable() {
                        var count by rememberSaveable { mutableStateOf(0) }
                        // Use 'count' in the composable function as usual
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Custom Keys for State Persistence",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, rememberSaveable uses the composable function’s position in the tree as the key for state persistence. However, you can provide a custom key to have more control over state restoration. This is especially useful when you have multiple instances of the same composable with different states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun MyComposable(id: String) {
                        var count by rememberSaveable(key = "count_$id") { mutableStateOf(0) }
                        // Use 'count' in the composable function as usual
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How would you manage state across recompositions and configuration changes in Compose-based screens?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use rememberSaveable for simple UI state (e.g., text input) — it stores data in a Bundle and survives configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use ViewModel for complex or shared state — ViewModel lives beyond recompositions and configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For surviving process death, use SavedStateHandle inside the ViewModel to persist critical state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use rememberSaveable for local UI state and ViewModel + SavedStateHandle for screen-level or recoverable business state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How to retain State across recomposition and configuration changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To retain state across recomposition, use remember; to persist it across configuration changes (like screen rotations), use rememberSaveable. rememberSaveable automatically saves the state in a Bundle (or custom saver) and restores it when the Activity or Composable is recreated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    var text by rememberSaveable { mutableStateOf("") }
                    """.trimIndent()
            )

            //https://github.com/shareef01/tabakpp.git
            KotlinCode(
                code = """
                     @Immutable
                     data class TabakSpacing(
                        val cardRounding: Dp = 16.dp, // Reduced from 24
                        val buttonRounding: Dp = 100.dp,
                        val minTouchTarget: Dp = 44.dp,
                        val bottomNavHeight: Dp = 64.dp,
                        val bottomNavPadding: Dp = 24.dp,
                        /** Extra scroll breathing room below the last item (viewport already clears the nav). */
                        val scrollBottomPadding: Dp = 24.dp
                     )

                     val LocalSpacing = staticCompositionLocalOf { TabakSpacing() }
                    
                     @Composable
                     fun AppScaffold(
                         collapseKey: String,
                         collapseEnabled: Boolean,
                         header: @Composable (isCollapsed: Boolean) -> Unit,
                         bottomNav: @Composable () -> Unit,
                         content: @Composable (PaddingValues) -> Unit
                     ) {
                         val spacing = LocalSpacing.current
                         val density = LocalDensity.current
                         
                         // Scroll tracking for collapsing header
                         var scrollOffset by rememberSaveable(collapseKey) { mutableStateOf(0f) }
                         val maxOffset = with(density) { 60.dp.toPx() } // Distance to trigger collapse
                         
                         val nestedScrollConnection = remember {
                             object : NestedScrollConnection {
                                 override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                                     val delta = available.y
                                     val newOffset = (scrollOffset + delta).coerceIn(-maxOffset, 0f)
                                     if (collapseEnabled) scrollOffset = newOffset
                                     return Offset.Zero
                                 }
                             }
                         }

                         val isCollapsed = collapseEnabled && scrollOffset <= -maxOffset * 0.5f
                         val navigationBottom = WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()
                         // Floating dock height (72) + dock margin — applied as viewport inset so content
                         // cannot paint underneath the pill. Extra scroll padding is separate breathing room.
                         val navClearance = 72.dp + spacing.bottomNavPadding + navigationBottom

                         Column(
                             modifier = Modifier
                                 .fillMaxSize()
                                 .background(MaterialTheme.colorScheme.background)
                                 .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Top + WindowInsetsSides.Horizontal))
                                 .nestedScroll(nestedScrollConnection)
                         ) {
                             // Sticky Header
                             header(isCollapsed)

                             Box(
                                 modifier = Modifier
                                     .weight(1f)
                                     .fillMaxWidth()
                             ) {
                                 // Inset the content viewport above the floating nav (not just scroll padding).
                                 Box(
                                     modifier = Modifier
                                         .fillMaxSize()
                                         .padding(bottom = navClearance)
                                 ) {
                                     content(PaddingValues(bottom = spacing.scrollBottomPadding))
                                 }

                                 // Fixed Bottom Nav — wrap content height so fillMaxSize inside the dock
                                 // cannot expand this overlay across the whole content pane.
                                 Box(
                                     modifier = Modifier
                                         .align(Alignment.BottomCenter)
                                         .fillMaxWidth()
                                         .wrapContentHeight()
                                         .padding(horizontal = 16.dp)
                                         .padding(bottom = spacing.bottomNavPadding)
                                         .navigationBarsPadding()
                                 ) {
                                     bottomNav()
                                 }
                             }
                         }
                     }
                """.trimIndent()
            )
        }
    }
}